// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { Alert, Box, Button, Typography } from "@outschool/backpack";
import { standardPayoutCents } from "@outschool/business-rules";
import {
  AccountSectionNewQuery,
  CancelAllOneOnOneSectionMeetingsMutation,
  CancelAllOneOnOneSectionMeetingsMutationVariables,
  FullCurrentUserFragmentFragment,
  OneOnOneEnrollmentRepriceMutationMutation,
  OneOnOneEnrollmentRepriceMutationMutationVariables,
  TransferEnrollmentMutation,
  TransferEnrollmentMutationVariables,
  TransferUnclaimedGiftEnrollmentMutation,
  TransferUnclaimedGiftEnrollmentMutationVariables,
  UnblockLearnerMutation,
  UnblockLearnerMutationVariables,
  WithdrawAndRefundEnrollmentMutation,
  WithdrawAndRefundEnrollmentMutationVariables,
  WithdrawAndRefundGiftEnrollmentMutation,
  WithdrawAndRefundGiftEnrollmentMutationVariables
} from "@outschool/gql-frontend-generated";
import {
  Trans,
  renderCurrencyString,
  useTranslation
} from "@outschool/localization";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import { useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import {
  Loading,
  ModalActionButtons,
  ModalProps,
  Select
} from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React from "react";
import { useHistory } from "react-router-dom";

import * as Enrollment from "../../../shared/Enrollment";
import * as EnrollmentPayment from "../../../shared/EnrollmentPayment";
import * as EnrollmentPaymentRefund from "../../../shared/EnrollmentPaymentRefund";
import { REASONS } from "../../../shared/EnrollmentPaymentRefund";
import * as Env from "../../../shared/Env";
import * as Routes from "../../../shared/Routes";
import * as User from "../../../shared/User";
import useAlertNotice from "../../hooks/useAlertNotice";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import {
  AccountSectionQuery,
  AccountSectionQueryAndVariables,
  useAccountSectionQuery
} from "../../queries/AccountSectionQuery";
import { unblockLearnerMutation } from "../../queries/BlockLearnerMutations";
import {
  EnrollmentListQuery,
  getEnrollmentListQueryVariables
} from "../../queries/EnrollmentListQuery";
import {
  oneOnOneEnrollmentRepriceMutation,
  transferEnrollmentMutation,
  transferUnclaimedGiftEnrollmentMutation,
  withdrawAndRefundEnrollmentMutation,
  withdrawAndRefundGiftEnrollmentMutation
} from "../../queries/EnrollmentMutations";
import { cancelAllOneOnOneSectionMeetingsMutation } from "../../queries/MeetingMutations";
import useTransferSections, {
  TransferSection,
  useMapAsOptions
} from "../../queries/TransferSectionQueries";
import StopEnrollmentPaymentSchedule from "../enrolled/StopEnrollmentPaymentSchedule";
import StopSubscription from "../enrolled/StopSubscription";
import ActivitySummary from "../enrolling/ActivitySummary";
import EnrollmentPaymentRefundForm from "../EnrollmentPaymentRefundForm";
import ErrorMessage from "../ErrorMessage";
import { NotFoundContent } from "../NotFoundContent";
import PageModal from "../PageModal";
import BlockLearnerModal from "./BlockLearnerModal";
import { LearnerBlockedBadge } from "./LearnerBlockedBadge";
import {
  EnrollmentPriceChangeConfirmation,
  OneOnOneEnrollmentReprice,
  repriceNoticeProps
} from "./OneOnOneRepriceModal";

enum ModalModes {
  BlockLearner = "BlockLearner",
  ManageEnrollment = "ManageEnrollment",
  ConfirmPriceChange = "ConfirmPriceChange"
}

type ModalState = {
  mode: ModalModes;
  price?: number;
};

export default function SectionLearnerModal({
  learnerOrEnrollmentUid,
  sectionUid,
  open,
  onClose
}: { learnerOrEnrollmentUid: string; sectionUid: string } & ModalProps) {
  const queryVariables = {
    uid: sectionUid,
    learnerOrEnrollmentUid,
    hasSelection: !!learnerOrEnrollmentUid
  };
  const rootQuery = { query: AccountSectionQuery, variables: queryVariables };
  const { loading, error, section, activity } = useAccountSectionQuery({
    variables: queryVariables,
    skip: !open
  });

  const canManageLearnerEnrollment = section?.currentUserIsOwner;
  const [modalState, setModalState] = React.useState<ModalState>({
    mode: ModalModes.ManageEnrollment
  });
  const { t } = useTranslation(
    "client\\components\\teacher\\SectionLearnerModal"
  );

  const { setNotice, clearNotice, notice } = useAlertNotice();

  const [repriceEnrollent, { loading: isUpdatingPrice }] = useMutation<
    OneOnOneEnrollmentRepriceMutationMutation,
    OneOnOneEnrollmentRepriceMutationMutationVariables
  >(oneOnOneEnrollmentRepriceMutation);

  if (!open) {
    return null;
  }

  if (loading) {
    return (
      <PageModal open={true} onClose={onClose}>
        <Loading delay={0} />
      </PageModal>
    );
  }

  if (!loading && !section && !activity && !error) {
    return (
      <PageModal open={true} onClose={onClose}>
        <NotFoundContent message="Section not found." />
      </PageModal>
    );
  }

  if (error) {
    return (
      <PageModal open={true} onClose={onClose}>
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      </PageModal>
    );
  }

  const { selectedEnrollment, learnerEnrollmentPayments } = section ?? {};
  // Gift enrollment payments have no associated learners, so learnerEnrollmentPayments comes back empty.
  // Fall back to the selected enrollment's enrollment payments, which are learner agnostic.
  const enrollmentPayments = learnerEnrollmentPayments?.length
    ? learnerEnrollmentPayments
    : selectedEnrollment?.payments?.length
    ? selectedEnrollment.payments
    : [];

  const onPriceUpdate = async ({
    price,
    confirmed = false
  }: {
    price: number;
    confirmed?: boolean;
  }) => {
    if (
      isUpdatingPrice ||
      !selectedEnrollment ||
      !selectedEnrollment.priceCents
    ) {
      return;
    }

    const prevPrice = selectedEnrollment.priceCents;
    if (price < selectedEnrollment.priceCents || confirmed) {
      const { data, errors } = await repriceEnrollent({
        variables: {
          enrollmentUid: selectedEnrollment.uid,
          newPriceCents: price
        }
      });

      if (!!errors) {
        const errorMessage = errors?.join(";");
        if (errorMessage) {
          setNotice({
            severity: "error",
            title: <>{t`Unable to update enrollment price`}</>,
            description: <>{errorMessage}</>
          });
        }
      }

      const {
        repriceTutoringEnrollment: { canceledMeetingUids }
      } = data!;
      setNotice(
        repriceNoticeProps({
          t,
          newPrice: price,
          oldPrice: prevPrice,
          confirmed,
          meetingsCanceled: canceledMeetingUids.length
        })
      );
      setModalState({ mode: ModalModes.ManageEnrollment });
    } else {
      setNotice(
        repriceNoticeProps({
          t,
          newPrice: price,
          oldPrice: prevPrice
        })
      );
      setModalState({
        mode: ModalModes.ConfirmPriceChange,
        price
      });
    }
  };

  const onBlockLearner = () => {
    setModalState({ mode: ModalModes.BlockLearner });
  };

  const handleBack = () => {
    clearNotice();
    setModalState({ mode: ModalModes.ManageEnrollment });
  };

  const handleClose = () => {
    if (!!onClose) {
      onClose();
    }

    handleBack();
  };

  return (
    <PageModal open={open} onClose={handleClose}>
      {modalState.mode === ModalModes.ConfirmPriceChange ? (
        <EnrollmentPriceChangeConfirmation
          notice={notice}
          onCancel={handleBack}
          onPriceUpdate={() =>
            onPriceUpdate({ price: modalState.price!, confirmed: true })
          }
        />
      ) : canManageLearnerEnrollment &&
        modalState.mode === ModalModes.ManageEnrollment ? (
        <ManageLearnerEnrollment
          activity={activity!}
          section={section}
          enrollment={selectedEnrollment}
          enrollmentPayments={enrollmentPayments as any}
          rootQuery={rootQuery}
          onBlockLearner={onBlockLearner}
          onPriceUpdate={onPriceUpdate}
          notice={notice}
        />
      ) : modalState.mode === ModalModes.BlockLearner ? (
        <BlockLearnerModal
          educatorUid={section?.leader.uid!}
          enrollment={selectedEnrollment}
          handleBack={handleBack}
          section={section!}
        />
      ) : (
        <OrgOwnerRestrictedFeature />
      )}
    </PageModal>
  );
}

interface ManageLearnerEnrollmentProps {
  activity: NonNullable<AccountSectionNewQuery["section"]>["activity"];
  section: AccountSectionNewQuery["section"];
  enrollment: NonNullable<
    AccountSectionNewQuery["section"]
  >["selectedEnrollment"];
  enrollmentPayments: NonNullable<
    AccountSectionNewQuery["section"]
  >["learnerEnrollmentPayments"];
  rootQuery: AccountSectionQueryAndVariables;
}

function ManageLearnerEnrollment({
  activity,
  section,
  enrollment,
  enrollmentPayments,
  rootQuery,
  onBlockLearner,
  onPriceUpdate,
  notice
}: ManageLearnerEnrollmentProps & {
  onBlockLearner: () => void;
  onPriceUpdate: ({
    price,
    confirmed
  }: {
    price: number;
    confirmed?: boolean;
  }) => void;
  notice: React.JSX.Element | null;
}) {
  const [unblockInProgress, setUnblockInProgress] = React.useState(false);
  const history = useHistory();
  const { timeZone: userTimeZone } = useTimeZone();
  const { currentUser } = useSession();
  const { setTimedTopNotice } = useTopNoticeContext();
  const { t } = useTranslation(
    "client\\components\\teacher\\SectionLearnerModal"
  );

  // @ts-ignore TS(2345): Argument of type '{ __typename?: "Enrollment" | un... Remove this comment to see the full error message
  const isOngoing = Enrollment.isOngoing(enrollment);
  const totalUnrefundedCents = _.sumBy(
    enrollmentPayments,
    enrollmentPayment => enrollmentPayment.unrefundedCents
  );
  const hasUnrefundedPayments = !!totalUnrefundedCents;
  // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
  const learnerName = enrollment.learner
    ? // @ts-ignore TS(2345): Argument of type '{ __typename?: "Enrollment" | un... Remove this comment to see the full error message
      Enrollment.displayName(enrollment, enrollment.learner)
    : "Gift";
  const canTransfer =
    // @ts-ignore TS(2345): Argument of type '{ __typename?: "Enrollment" | un... Remove this comment to see the full error message
    Enrollment.isValid(enrollment) && (!isOngoing || !enrollment.isEnded);
  const isLearnerBlocked = enrollment?.learner?.isBlocked;
  const isReportingUser =
    currentUser?.uid === enrollment?.learner?.blockedByUserUid;
  const isAdmin = User.isAdmin(currentUser);

  const isOneOnOne = activity.hasTeacherSchedule;
  // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
  const showWithdrawButton = enrollment.canBeWithdrawn || isOneOnOne;
  // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
  const showStopButton = !enrollment.canBeWithdrawn && enrollment.canBeStopped;

  const modalHeading =
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    enrollment.__typename === "GiftEnrollment"
      ? learnerName
      : `${learnerName}, age ${Enrollment.displayAge(
          // @ts-ignore TS(2345): Argument of type '{ __typename?: "Enrollment" | un... Remove this comment to see the full error message
          enrollment,
          // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
          enrollment.learner
        )}`;

  const [unblockLearner] = useMutation<
    UnblockLearnerMutation,
    UnblockLearnerMutationVariables
  >(unblockLearnerMutation, {
    refetchQueries: [
      {
        query: AccountSectionQuery,
        variables: {
          uid: section?.uid,
          learnerOrEnrollmentUid: enrollment?.learner?.uid,
          hasSelection: !!enrollment?.learner?.uid
        }
      }
    ]
  });

  const onUnblockLearner = async () => {
    if (!enrollment?.learner?.uid) {
      throw new Error("No learner UID.");
    }
    setUnblockInProgress(true);
    await unblockLearner({
      variables: {
        educatorUid: activity.user_uid,
        learnerUid: enrollment.learner.uid
      }
    });
    setUnblockInProgress(false);
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: "1em" }}>
        <Typography variant="h3" data-private>
          {modalHeading}
        </Typography>
        {isLearnerBlocked && <LearnerBlockedBadge />}
      </Box>
      <hr style={{ marginTop: "1em" }} />
      {isLearnerBlocked ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1em",
            alignItems: "start"
          }}
        >
          <Box>
            <Typography variant="h6">
              {isReportingUser
                ? t("This learner is blocked by you.")
                : t("This learner is blocked.")}
            </Typography>
            <Typography>
              On{" "}
              {Time.formatDateTimeWithFullWeekday(
                enrollment.learner?.blockedAt,
                userTimeZone,
                true
              )}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            data-test-id="LearnerNotes-BlockLearnerButton"
            data-private
            onClick={onUnblockLearner}
            disabled={!(isReportingUser || isAdmin) || unblockInProgress}
          >
            {unblockInProgress
              ? t("Unblocking...")
              : t("Unblock {{learnerName}}", { learnerName })}
          </Button>
        </Box>
      ) : (
        <>
          <Box sx={{ marginBottom: "1em" }}>
            <Typography variant="h6" data-private>
              {t("Adults attending classes as {{learnerName}}?", {
                learnerName
              })}
            </Typography>
            <Typography>
              <Trans t={t}>
                Blocking a learner means they{" "}
                <b>lose access to all Outschool classes</b>. We’ll notify their
                family and Trust & Safety.
              </Trans>
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="error"
            data-test-id="LearnerNotes-BlockLearnerButton"
            data-private
            onClick={onBlockLearner}
          >
            {t("Block {{learnerName}}", { learnerName })}
          </Button>
        </>
      )}
      {isOneOnOne && !!enrollment?.priceCents && !!activity.price_cents && (
        <>
          <hr />
          <OneOnOneEnrollmentReprice
            activityPrice={activity.price_cents}
            enrollmentPrice={enrollment.priceCents}
            onPriceUpdate={onPriceUpdate}
            notice={notice}
          />
        </>
      )}
      {canTransfer && (
        <React.Fragment>
          <TransferEnrollment
            activity={activity}
            section={section}
            enrollment={enrollment}
            learnerName={learnerName}
            userTimeZone={userTimeZone}
          />
        </React.Fragment>
      )}
      {showStopButton && (
        <React.Fragment>
          <hr />
          {activity.isClub ? (
            <StopEnrollmentPaymentSchedule
              studentName={learnerName}
              // @ts-ignore TS(2322): Type '{ __typename?: "Enrollment" | undefined; has... Remove this comment to see the full error message
              enrollment={enrollment}
              activity={activity}
              rootQuery={rootQuery}
            />
          ) : (
            <StopSubscription
              activity={activity}
              section={section}
              studentName={learnerName}
              // @ts-ignore TS(2322): Type '{ __typename?: "Enrollment" | undefined; has... Remove this comment to see the full error message
              enrollment={enrollment}
              userTimeZone={userTimeZone}
              buttonLabel={`Stop subscription for ${learnerName} to this class`}
              isTeacher={true}
            />
          )}
          <hr />
        </React.Fragment>
      )}
      {showWithdrawButton && (
        <React.Fragment>
          <hr id="showWithdrawButton" />
          <WithdrawAndRefund
            activity={activity}
            section={section}
            enrollment={enrollment}
            // @ts-ignore TS(2322): Type '{ __typename?: "User" | undefined; uid: stri... Remove this comment to see the full error message
            currentUser={currentUser}
            hasUnrefundedPayments={hasUnrefundedPayments}
            onSuccess={() => {
              history.push(Routes.sectionLearnersPath(section));
              setTimedTopNotice(
                isOneOnOne
                  ? `All meetings for ${learnerName} have been cancelled.`
                  : hasUnrefundedPayments
                  ? `${learnerName} has been withdrawn from this section.`
                  : // @ts-ignore TS(2531): Object is possibly 'null'.
                    `${learnerName} has been refunded and withdrawn from this section. We will send a refund confirmation to ${currentUser.email}.`
              );
            }}
            learnerName={learnerName}
          />
          <hr />
        </React.Fragment>
      )}
      <EnrollmentPaymentSummary
        activity={activity}
        enrollment={enrollment}
        enrollmentPayments={enrollmentPayments}
        section={section}
        userTimeZone={userTimeZone}
      />
    </>
  );
}

function TransferEnrollment({
  activity,
  userTimeZone,
  section,
  learnerName,
  enrollment
}: {
  activity: NonNullable<AccountSectionNewQuery["section"]>["activity"];
  userTimeZone: string;
  section: AccountSectionNewQuery["section"];
  learnerName: string;
  enrollment: NonNullable<
    AccountSectionNewQuery["section"]
  >["selectedEnrollment"];
}) {
  const { t } = useTranslation(
    "client\\components\\teacher\\SectionLearnerModal"
  );
  const history = useHistory();
  const { setTopNotice } = useTopNoticeContext();
  const [inProgress, setInProgress] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | string | null>();
  // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
  const [newSection, setNewSection] = React.useState<TransferSection>(null);
  // @ts-ignore TS(2345): Argument of type '{ __typename?: "Section" | undef... Remove this comment to see the full error message
  const { loading, sections } = useTransferSections(activity, section);

  const [transferEnrollment] = useMutation<
    TransferEnrollmentMutation,
    TransferEnrollmentMutationVariables
  >(transferEnrollmentMutation, {
    refetchQueries: [
      {
        query: EnrollmentListQuery,
        // @ts-ignore TS(2531): Object is possibly 'null'.
        variables: getEnrollmentListQueryVariables(section.uid)
      }
    ]
  });

  const [transferUnclaimedGiftEnrollment] = useMutation<
    TransferUnclaimedGiftEnrollmentMutation,
    TransferUnclaimedGiftEnrollmentMutationVariables
  >(transferUnclaimedGiftEnrollmentMutation, {
    refetchQueries: [
      {
        query: EnrollmentListQuery,
        // @ts-ignore TS(2531): Object is possibly 'null'.
        variables: getEnrollmentListQueryVariables(section.uid)
      }
    ]
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (inProgress) {
      return;
    }

    if (!newSection) {
      setError(t("Please select the target section"));
      return;
    }

    setInProgress(true);

    const newSectionUid = newSection.uid;
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    const enrollmentUid = enrollment.uid;

    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    if (enrollment.__typename === "GiftEnrollment") {
      try {
        await transferUnclaimedGiftEnrollment({
          variables: { enrollmentUid, newSectionUid }
        });
        history.push(Routes.sectionLearnersPath(section));
        setTopNotice(t("This learner has been transferred."));
      } catch (queryError) {
        (queryError: $TSFixMe) => setError(queryError);
      }
    } else {
      try {
        await transferEnrollment({
          variables: { enrollmentUid, newSectionUid }
        });
        history.push(Routes.sectionLearnersPath(section));
        setTopNotice(t("This learner has been transferred."));
      } catch (queryError) {
        setError(queryError);
      }
    }

    setInProgress(false);
  };

  const options = useMapAsOptions(activity, sections, userTimeZone);

  if (loading || sections.length < 1) {
    return null;
  }

  return (
    <>
      <hr />
      <div>
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
        <Box sx={{ marginBottom: "1em" }}>
          <Typography variant="h6" data-private>
            {t("Transfer {{learnerName}} to another section", { learnerName })}
          </Typography>
          <Typography>
            {t(
              "Please confirm this change with the buyer before you transfer."
            )}
          </Typography>
        </Box>
        <form onSubmit={handleSubmit}>
          <Select
            value={newSection}
            onChange={(val: TransferSection) => setNewSection(val)}
            options={options}
            placeholder={t("Select a section")}
            sx={{
              width: "100%",
              boxSizing: "border-box",
              maxWidth: "100%",
              marginBottom: "1em"
            }}
          ></Select>
          {!!newSection &&
            !!enrollment?.paymentSchedule &&
            enrollment?.priceCents && (
              <Alert
                title="Transfer notice"
                severity="warning"
                sx={{ marginBottom: "16px" }}
              >
                {t(
                  "This learner is paying weekly for this class. They will be charged a maximum \
              of the enrollment price of {{enrollmentPriceString}}, even if they \
              are being transferred to a section that has not started yet.",
                  {
                    enrollmentPriceString: renderCurrencyString({
                      priceInCents: enrollment.priceCents
                    })
                  }
                )}
              </Alert>
            )}
          <Button
            type="submit"
            data-private
            disabled={enrollment?.learner?.isBlocked || inProgress}
            variant="contained"
          >
            {inProgress
              ? t("Transferring...")
              : t("Transfer {{learnerName}}", { learnerName })}
          </Button>
        </form>
      </div>
    </>
  );
}

function EnrollmentPaymentSummary({
  activity,
  enrollment,
  enrollmentPayments,
  section,
  userTimeZone
}: {
  activity: NonNullable<AccountSectionNewQuery["section"]>["activity"];
  enrollment: NonNullable<
    AccountSectionNewQuery["section"]
  >["selectedEnrollment"];
  enrollmentPayments: NonNullable<
    AccountSectionNewQuery["section"]
  >["learnerEnrollmentPayments"];
  section: AccountSectionNewQuery["section"];
  userTimeZone: string;
}) {
  const isOngoing = !!activity.is_ongoing_weekly;
  const enrollmentPaymentRefunds = _.flatten(
    // @ts-ignore TS(2532): Object is possibly 'undefined'.
    enrollmentPayments.map(ep => ep.refunds)
  );

  const totalUnrefundedCents =
    EnrollmentPayment.totalUnrefundedCents(
      // @ts-ignore TS(2345): Argument of type '{ __typename?: "EnrollmentPaymen... Remove this comment to see the full error message
      enrollmentPayments,
      enrollmentPaymentRefunds
      // @ts-ignore TS(2345): Argument of type '{ __typename?: "EnrollmentPaymen... Remove this comment to see the full error message
    ) - EnrollmentPayment.totalTeacherDiscountCents(enrollmentPayments);
  // @ts-ignore TS(2345): Argument of type '{ __typename?: "EnrollmentPaymen... Remove this comment to see the full error message
  const totalPaidCents = EnrollmentPayment.totalPaidCents(enrollmentPayments);
  const grossTeacherPayoutCents =
    totalPaidCents -
    EnrollmentPaymentRefund.totalCentsFromTeacherPayout(
      enrollmentPaymentRefunds
    ) -
    // @ts-ignore TS(2345): Argument of type '{ __typename?: "EnrollmentPaymen... Remove this comment to see the full error message
    EnrollmentPayment.totalTeacherDiscountCents(enrollmentPayments);

  // TODO: Base ongoing total price off of weeks instead of enrollmentPayments.length
  const totalPrice = isOngoing
    ? // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
      enrollment.priceCents * enrollmentPayments.length
    : // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
      enrollment.priceCents;

  const { t } = useTranslation(
    "client\\components\\teacher\\SectionLearnerModal"
  );

  return (
    <div>
      <Typography variant="h6" sx={{ marginBottom: "0.5em" }}>
        {t("Refund")}
      </Typography>
      <div>
        Enrollment price:{" "}
        {isOngoing ? (
          <span>
            {renderCurrencyString({
              // @ts-ignore TS(2322): Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
              priceInCents: enrollment.priceCents,
              useFree: true
            })}{" "}
            {/* @ts-ignore TS(2532): Object is possibly 'undefined'. */}*{" "}
            {enrollmentPayments.length} weeks ={" "}
            {/* @ts-ignore TS(2322): Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message */}
            {renderCurrencyString({ priceInCents: totalPrice })}
          </span>
        ) : (
          // @ts-ignore TS(2322): Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
          renderCurrencyString({ priceInCents: totalPrice })
        )}
      </div>
      {grossTeacherPayoutCents !== totalPrice && (
        <div>
          Effective price (after teacher refunds/discounts):{" "}
          {renderCurrencyString({
            priceInCents: grossTeacherPayoutCents,
            useFree: true
          })}
        </div>
      )}
      <Box>
        Teacher earnings:{" "}
        {renderCurrencyString({
          priceInCents: standardPayoutCents(
            grossTeacherPayoutCents,
            // @ts-ignore TS(2345): Argument of type '{ __typename?: "Section" | undef... Remove this comment to see the full error message
            section
          )
        })}
      </Box>
      <hr />
      {/* @ts-ignore TS(2532): Object is possibly 'undefined'. */}
      {enrollmentPayments.map(enrollmentPayment => (
        <PaymentAndRefunds
          key={enrollmentPayment.uid}
          enrollmentPayment={enrollmentPayment}
          enrollment={enrollment}
          section={section}
          userTimeZone={userTimeZone}
        />
      ))}
      {/* @ts-ignore TS(2532): Object is possibly 'undefined'. */}
      {enrollmentPayments.length > 1 && (
        <div>
          {totalUnrefundedCents < totalPaidCents ? "Net" : "Total"}
          {" paid (after teacher and Outschool refunds): "}
          {renderCurrencyString({ priceInCents: totalUnrefundedCents })}
        </div>
      )}
    </div>
  );
}

function PaymentAndRefunds({
  enrollmentPayment,
  section,
  enrollment,
  userTimeZone
}: {
  enrollmentPayment: NonNullable<
    AccountSectionNewQuery["section"]
    // @ts-ignore TS(2537): Type '{ __typename?: "EnrollmentPayment" | undefin... Remove this comment to see the full error message
  >["learnerEnrollmentPayments"][number];
  section: AccountSectionNewQuery["section"];
  enrollment: NonNullable<
    AccountSectionNewQuery["section"]
  >["selectedEnrollment"];
  userTimeZone: string;
}) {
  let buyerName = "";
  if (enrollmentPayment.organization) {
    buyerName = "learner's school";
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
  } else if (enrollment.parent) {
    // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
    buyerName = enrollment.parent.name;
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
  } else if (enrollment.__typename === "GiftEnrollment" && enrollment.buyer) {
    // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
    buyerName = enrollment.buyer.name;
  }

  const amountPaidString = renderCurrencyString({
    priceInCents: enrollmentPayment.amount_cents
  });

  return (
    <Box sx={{ paddingBottom: "0.5em" }}>
      {EnrollmentPayment.isTeacherCouponPayment(enrollmentPayment) ? (
        <div>
          {amountPaidString} Coupon Code {enrollmentPayment.couponCode.code}{" "}
          applied
        </div>
      ) : (
        <div>
          <Box sx={{ paddingBottom: "0.5em" }}>
            {!!enrollmentPayment.amount_credits ? (
              <Typography variant="subtitle1">
                {`${enrollmentPayment.amount_credits} credits (Equivalent Value: ${amountPaidString})`}
              </Typography>
            ) : (
              <Typography variant="subtitle1">{amountPaidString}</Typography>
            )}
            <Typography variant="body1" sx={{ color: "neutral.600" }}>
              Paid by {buyerName} on{" "}
              {Time.formatDate(enrollmentPayment.paid_at, userTimeZone)}
              {!!enrollmentPayment.oneOnOneMeeting?.start_time && (
                <Typography>
                  {dayjs().isBefore(
                    enrollmentPayment.oneOnOneMeeting?.start_time
                  )
                    ? ",  Meet on "
                    : ",  Met on "}
                  {Time.formatDateTime(
                    enrollmentPayment.oneOnOneMeeting.start_time,
                    userTimeZone
                  )}
                </Typography>
              )}
              {!!enrollmentPayment.start_time &&
                !enrollmentPayment.oneOnOneMeeting && (
                  <Typography>
                    {", for the week of "}
                    {Time.formatDate(
                      enrollmentPayment.start_time,
                      userTimeZone
                    )}
                  </Typography>
                )}
            </Typography>
          </Box>
          <div>
            {!enrollmentPayment.disbursed_at && (
              <EnrollmentPaymentRefundForm
                enrollmentPayment={enrollmentPayment}
                // @ts-ignore TS(2322): Type '{ __typename?: "Section" | undefined; filled... Remove this comment to see the full error message
                section={section}
                // @ts-ignore TS(2322): Type '{ __typename?: "Enrollment" | undefined; has... Remove this comment to see the full error message
                enrollment={enrollment}
                isAdmin={false}
              />
            )}
            {enrollmentPayment.refunds.map(
              (enrollmentPaymentRefund: $TSFixMe) => {
                const amountRefundedString =
                  !!enrollmentPaymentRefund.amount_credits
                    ? `${enrollmentPaymentRefund.amount_credits} credits`
                    : renderCurrencyString({
                        priceInCents: enrollmentPaymentRefund.amount_cents
                      });

                return (
                  <Typography
                    key={enrollmentPaymentRefund.uid}
                    sx={{ color: "neutral.600" }}
                  >
                    {" "}
                    ({amountRefundedString}{" "}
                    {enrollmentPaymentRefund.reduces_teacher_payout
                      ? `refunded from price`
                      : "refunded from Outschool funds"}
                    <span>
                      {" by "}
                      {enrollmentPaymentRefund.user_role}
                      <Typography
                        variant="inherit"
                        sx={{
                          whitSpace: "nowrap"
                        }}
                      >
                        {" on "}
                        {enrollmentPaymentRefund.refunded_at
                          ? Time.formatDate(
                              enrollmentPaymentRefund.refunded_at,
                              userTimeZone
                            )
                          : "Unspecified date"}
                      </Typography>
                      )
                    </span>
                  </Typography>
                );
              }
            )}
          </div>
        </div>
      )}
    </Box>
  );
}

function WithdrawAndRefund({
  enrollment,
  activity,
  section,
  hasUnrefundedPayments,
  learnerName,
  onSuccess
}: {
  enrollment: NonNullable<
    AccountSectionNewQuery["section"]
  >["selectedEnrollment"];
  activity: NonNullable<AccountSectionNewQuery["section"]>["activity"];
  section: AccountSectionNewQuery["section"];
  hasUnrefundedPayments: boolean;
  learnerName: string;
  currentUser: FullCurrentUserFragmentFragment;
  onSuccess: () => void;
}) {
  const [open, setOpen] = React.useState<boolean>(false);
  const [refundReason, setRefundReason] = React.useState<string | null>();
  const [refundNote, setRefundNote] = React.useState<string | null>();
  const [mutationInProgress, setMutationInProgress] =
    React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | string | null>();
  const [refundAllRemaining, setRefundAllRemaining] = React.useState<
    boolean | null
  >();

  const [withdrawAndRefundEnrollment] = useMutation<
    WithdrawAndRefundEnrollmentMutation,
    WithdrawAndRefundEnrollmentMutationVariables
  >(withdrawAndRefundEnrollmentMutation, {
    variables: {
      // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
      enrollmentUid: enrollment.uid,
      // @ts-ignore TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
      refundReason,
      // @ts-ignore TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
      refundNote,
      refundAllRemaining
    },
    refetchQueries: [
      {
        query: EnrollmentListQuery,
        // @ts-ignore TS(2531): Object is possibly 'null'.
        variables: getEnrollmentListQueryVariables(section.uid)
      }
    ]
  });

  const [cancelAllOneOnOneSectionMeetings] = useMutation<
    CancelAllOneOnOneSectionMeetingsMutation,
    CancelAllOneOnOneSectionMeetingsMutationVariables
  >(cancelAllOneOnOneSectionMeetingsMutation, {
    variables: {
      // @ts-ignore TS(2531): Object is possibly 'null'.
      sectionUid: section.uid,
      // @ts-ignore TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
      cancelReason: refundReason,
      cancelNote: refundNote,
      reducesTeacherPayout: true
    }
  });

  const [withdrawAndRefundGiftEnrollment] = useMutation<
    WithdrawAndRefundGiftEnrollmentMutation,
    WithdrawAndRefundGiftEnrollmentMutationVariables
  >(withdrawAndRefundGiftEnrollmentMutation, {
    variables: {
      // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
      enrollmentUid: enrollment.uid,
      // @ts-ignore TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
      refundReason,
      // @ts-ignore TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
      refundNote,
      refundAllRemaining
    },
    refetchQueries: [
      {
        query: EnrollmentListQuery,
        // @ts-ignore TS(2531): Object is possibly 'null'.
        variables: getEnrollmentListQueryVariables(section.uid)
      }
    ]
  });

  const { t } = useTranslation(
    "client\\components\\teacher\\SectionLearnerModal"
  );

  const refundReasonInput = React.useRef<HTMLSelectElement>();
  const refundNoteInput = React.useRef<HTMLTextAreaElement>();

  const isOneOnOne = activity.hasTeacherSchedule;

  const handleSubmit = (e: React.FormEvent) => {
    e && e.preventDefault();
    setError(undefined);
    if (!refundReason) {
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      refundReasonInput.current.focus();
      setError("Please choose a reason");
      return false;
    }
    if (!refundNote) {
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      refundNoteInput.current.focus();
      setError("Please add a note with more details");
      return false;
    }
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    if (enrollment.__typename === "GiftEnrollment") {
      setMutationInProgress(true);
      return withdrawAndRefundGiftEnrollment().then(
        () => {
          onSuccess && onSuccess();
        },
        error => {
          setMutationInProgress(false);
          setError(error);
        }
      );
    }

    if (isOneOnOne) {
      setMutationInProgress(true);
      return cancelAllOneOnOneSectionMeetings().then(
        () => {
          onSuccess && onSuccess();
        },
        error => {
          setMutationInProgress(false);
          setError(error);
        }
      );
    }

    setMutationInProgress(true);
    return withdrawAndRefundEnrollment().then(
      () => {
        onSuccess && onSuccess();
      },
      error => {
        setMutationInProgress(false);
        setError(error);
      }
    );
  };

  const withdrawAndRefundLabel = `Withdraw ${learnerName} ${
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    !!hasUnrefundedPayments && !!enrollment.refundableAmountCents
      ? " and refund..."
      : ""
  }`;
  const withdrawAndRefundAllLabel = `Withdraw ${learnerName} with full refund...`;
  const withdrawModalLabel = isOneOnOne
    ? t("Cancel all meetings")
    : refundAllRemaining
    ? withdrawAndRefundAllLabel
    : withdrawAndRefundLabel;

  const isTimeSensitive =
    // @ts-ignore TS(2531): Object is possibly 'null'.
    dayjs(section.start_time).isAfter(dayjs()) &&
    // @ts-ignore TS(2531): Object is possibly 'null'.
    dayjs(section.start_time).diff(dayjs(), "hours") <= 24;

  const [showConfirmation, setShowConfirmation] =
    React.useState(isTimeSensitive);

  const onClose = () => {
    setOpen(false);
    setShowConfirmation(isTimeSensitive);
  };

  // Teacher is able to withdraw and fully refund as long as there's any refundable amount
  const hasUndisbursedPayments = _.some(
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    enrollment.payments,
    payment => !payment.disbursed_at
  );
  const canWithdrawWithFullRefund =
    !activity.is_ongoing_weekly &&
    hasUndisbursedPayments &&
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    enrollment.refundableAmountCents > 0;

  let explanationPlaceHolder = "Explanation for...";

  // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
  if (enrollment.__typename === "GiftEnrollment") {
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    explanationPlaceHolder = `Explanation for ${enrollment.buyer.name}`;
  } else {
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    explanationPlaceHolder = `Explanation for ${enrollment.parent.name}`;
  }

  const refundAmountString = renderCurrencyString({
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    priceInCents: enrollment.refundableAmountCents
  });

  // @ts-ignore TS(2531): Object is possibly 'null'.
  const upcomingMeetings = section.meetings.filter(
    m => dayjs(m.start_time).isAfter() && !m.deleted_at
  );
  const upcomingMeetingCount = upcomingMeetings.length;

  const upcomingPaidMeetings = upcomingMeetings.filter(m => m.paidAt);
  const upcomingPaidMeetingCount = upcomingPaidMeetings.length;

  const upcomingPaidMeetingCents = _.sumBy(
    upcomingPaidMeetings,
    "refundableAmountCents"
  );

  const upcomingPaidMeetingAmount = renderCurrencyString({
    priceInCents: upcomingPaidMeetingCents,
    showCurrencyCode: false
  });

  // Should hide cancel button for 1-on-1 sections with existing future meetings
  const shouldHideCancelButton = upcomingMeetingCount === 0 && isOneOnOne;

  return (
    <React.Fragment>
      {isOneOnOne && shouldHideCancelButton ? (
        <Typography>
          {t("No future meetings to cancel for this enrollment.")}
        </Typography>
      ) : isOneOnOne ? (
        <Box
          flex
          sx={{
            flexDirection: "column",
            marginBottom: "0.5em"
          }}
        >
          <Typography
            variant="h5"
            sx={{
              marginBottom: "0.5em"
            }}
          >
            {t("Cancel all upcoming meetings")}
          </Typography>
          <Typography
            sx={{
              marginBottom: "0.3em"
            }}
          >
            {t("Upcoming: {{upcomingMeetingCount}} meetings", {
              upcomingMeetingCount
            })}
          </Typography>
          <Typography
            sx={{
              marginBottom: "1em"
            }}
          >
            {t(
              "Paid: {{upcomingPaidMeetingCount}} meetings ({{upcomingPaidMeetingAmount}} paid)",
              {
                upcomingPaidMeetingCount,
                upcomingPaidMeetingAmount
              }
            )}
          </Typography>
          <Button
            onClick={() => {
              setOpen(true);
              setRefundAllRemaining(false);
            }}
            sx={{
              width: "fit-content"
            }}
          >
            {t("Cancel all meetings")}
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography data-private variant="h6">
            {t("Withdraw {{learnerName}}", {
              learnerName
            })}
          </Typography>
          <Button
            onClick={() => {
              setOpen(true);
              setRefundAllRemaining(false);
            }}
            sx={{ marginY: "1em" }}
          >
            {withdrawAndRefundLabel}
          </Button>
          <Typography sx={{ display: "block", marginBottom: "1em" }}>
            {/* @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'. */}
            {enrollment.refundableAmountCents > 0
              ? `This will issue a refund of ${refundAmountString}.`
              : "This withdrawal will not issue a refund."}
          </Typography>
          <Box>
            {/* @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'. */}
            {enrollment.refundPolicy.name} Refund Policy -{" "}
            {/* @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'. */}
            {enrollment.refundPolicy.description}
          </Box>
          {canWithdrawWithFullRefund && (
            <Button
              data-test-id="withdraw-full-refund"
              onClick={() => {
                setOpen(true);
                setRefundAllRemaining(true);
              }}
            >
              {withdrawAndRefundAllLabel}
            </Button>
          )}
        </Box>
      )}
      <PageModal open={open} onClose={onClose}>
        {showConfirmation ? (
          <React.Fragment>
            <Typography
              variant="h2"
              sx={{
                marginBottom: "0.5em"
              }}
            >
              Last Minute Withdrawal
            </Typography>
            <Box
              sx={{
                marginBottom: "1em"
              }}
            >
              This class is scheduled to start in less than 24 hours.
              Withdrawing a learner at the last minute can be frustrating for
              parents and learners and lead to poor reviews.
            </Box>
            <Box
              sx={{
                marginBottom: "1em"
              }}
            >
              We understand that sometimes this can&apos;t be helped and
              recommend only doing this when there is no other alternative.
            </Box>
            <Box
              sx={{
                marginBottom: "1em"
              }}
            >
              Are you sure you want to proceed?
            </Box>
            <ModalActionButtons
              trackingName="late_withdraw_learner_modal"
              primaryVariant="danger"
              onPrimary={() => {
                setShowConfirmation(false);
              }}
              primaryLabel={"Proceed"}
              cancelLabel="Cancel"
              onCancel={onClose}
            />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <ActivitySummary
              activity={activity}
              // @ts-ignore TS(2531): Object is possibly 'null'.
              startTime={section.start_time}
              // @ts-ignore TS(2531): Object is possibly 'null'.
              endTime={section.end_time}
              // @ts-ignore TS(2531): Object is possibly 'null'.
              nextOngoingMeeting={section.nextOngoingMeeting}
            />
            <Typography
              variant="h3"
              sx={{
                marginTop: "0.5em",
                marginBottom: "0.5em"
              }}
            >
              {isOneOnOne
                ? t("Cancel all meetings")
                : t("Withdraw {{learnerName}} from this section", {
                    learnerName: learnerName
                  })}
            </Typography>
            <ErrorMessage
              value={error}
              showStatusPageLink={Env.IS_READ_ONLY_MODE}
            />
            <form onSubmit={handleSubmit}>
              {isOneOnOne
                ? t("Why are you cancelling all meetings for this learner?")
                : t("Why are you withdrawing this learner?")}
              <Select
                name="refundReason"
                options={
                  isOneOnOne
                    ? EnrollmentPaymentRefund.TEACHER_ONE_ON_ONE_CANCEL_REASON_OPTIONS
                    : EnrollmentPaymentRefund.TEACHER_WITHDRAW_REASON_OPTIONS
                }
                // @ts-ignore TS(2322): Type 'MutableRefObject<HTMLSelectElement | undefin... Remove this comment to see the full error message
                selectRef={refundReasonInput}
                value={refundReason}
                placeholder="Choose a reason..."
                onChange={(value: string) => {
                  setRefundReason(value);
                  setError(null);
                }}
                sx={{
                  width: "100%",
                  boxSizing: "border-box",
                  marginBottom: "0.5em"
                }}
              ></Select>
              <div>
                {isOneOnOne
                  ? t("Please explain this cancellation to the parent:")
                  : t("Please explain this withdrawal to the parent:")}
              </div>
              <textarea
                // @ts-ignore TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
                value={refundNote}
                placeholder={explanationPlaceHolder}
                onChange={e => {
                  setRefundNote(e.target.value);
                  setError(null);
                }}
                name="refundNoteInput"
                // @ts-ignore TS(2322): Type 'MutableRefObject<HTMLTextAreaElement | undef... Remove this comment to see the full error message
                ref={refundNoteInput}
                rows={2}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  marginBottom: "0.5em",
                  height: "auto"
                }}
              ></textarea>
              <ModalActionButtons
                trackingName="withdraw_learner_modal"
                onCancel={onClose}
                primaryLabel={
                  mutationInProgress
                    ? isOneOnOne
                      ? t("Cancelling...")
                      : t("Withdrawing...")
                    : withdrawModalLabel
                }
                primaryType="submit"
                disabled={
                  mutationInProgress ||
                  !refundReason ||
                  (refundReason === REASONS.OTHER && !refundNote)
                }
              />
            </form>
          </React.Fragment>
        )}
      </PageModal>
    </React.Fragment>
  );
}

function OrgOwnerRestrictedFeature() {
  return (
    <>
      <Typography variant="h3">This feature is restricted</Typography>
      <Box
        sx={{
          mt: "1em"
        }}
      >
        Please contact your organization&apos;s administrator for help.
      </Box>
    </>
  );
}
